import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Plus, Edit, Trash2, Search, X } from 'lucide-react';

const RoutingMaster = () => {
  const [items, setItems] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const emptyForm = { product_sku: '', product_name: '', steps: [] };
  const [formData, setFormData] = useState({ ...emptyForm });
  const [stepInput, setStepInput] = useState({ step_no: 1, operation: '', machine_id: '', machine_name: '', cycle_time_sec: 0, setup_time_min: 0 });

  useEffect(() => { fetchData(); }, []);
  const fetchData = async () => {
    try {
      const [rRes, iRes, mRes] = await Promise.all([api.get('/production/routing'), api.get('/sales/inventory'), api.get('/production/machines')]);
      setItems(rRes.data); setInventory(iRes.data); setMachines(mRes.data);
    } catch(e){} finally { setLoading(false); }
  };

  const openCreate = () => { setEditing(null); setFormData({ ...emptyForm }); setShowModal(true); };
  const openEdit = (r) => { setEditing(r); setFormData({ product_sku: r.product_sku, product_name: r.product_name || '', steps: r.steps || [] }); setShowModal(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/production/routing/${editing.id}`, formData);
      else await api.post('/production/routing', formData);
      setShowModal(false); fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id, code) => {
    if (!window.confirm(`Delete routing "${code}"?`)) return;
    try { await api.delete(`/production/routing/${id}`); fetchData(); } catch(e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const addStep = () => {
    if (!stepInput.operation) return;
    const mc = machines.find(m => m.id === stepInput.machine_id);
    const newStep = { ...stepInput, machine_name: mc?.name || '', step_no: formData.steps.length + 1 };
    setFormData({ ...formData, steps: [...formData.steps, newStep] });
    setStepInput({ step_no: formData.steps.length + 2, operation: '', machine_id: '', machine_name: '', cycle_time_sec: 0, setup_time_min: 0 });
  };

  const removeStep = (idx) => {
    const updated = formData.steps.filter((_, i) => i !== idx).map((s, i) => ({...s, step_no: i + 1}));
    setFormData({ ...formData, steps: updated });
  };

  const onProductSelect = (sku) => {
    const inv = inventory.find(i => i.sku === sku);
    setFormData({ ...formData, product_sku: sku, product_name: inv?.name || '' });
  };

  const filtered = items.filter(r => r.routing_code?.toLowerCase().includes(searchTerm.toLowerCase()) || r.product_name?.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div data-testid="routing-page">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Routing Master</h1>
        <button onClick={openCreate} className="btn btn-primary" data-testid="add-routing-btn"><Plus size={16} /> Add Routing</button>
      </div>
      <div className="card mb-4">
        <div style={{ position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
          <input type="text" className="form-input" placeholder="Search routing..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ paddingLeft: '40px' }} data-testid="search-input" />
        </div>
      </div>
      <div className="card">
        {loading ? <div>Loading...</div> : (
          <div className="table-container">
            <table>
              <thead><tr><th>Code</th><th>Product SKU</th><th>Product Name</th><th>Steps</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {filtered.length === 0 ? <tr><td colSpan={6} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-tertiary)' }}>No routings found</td></tr> : filtered.map(r => (
                  <tr key={r.id} data-testid={`routing-row-${r.routing_code}`}>
                    <td className="font-mono">{r.routing_code}</td>
                    <td className="font-mono">{r.product_sku}</td>
                    <td>{r.product_name}</td>
                    <td>{(r.steps || []).length} steps</td>
                    <td><span className={`badge ${r.status === 'active' ? 'badge-success' : 'badge-danger'}`}>{r.status}</span></td>
                    <td>
                      <div className="flex gap-2">
                        <button onClick={() => openEdit(r)} className="btn btn-ghost btn-sm" data-testid={`edit-btn-${r.routing_code}`}><Edit size={14} /></button>
                        <button onClick={() => handleDelete(r.id, r.routing_code)} className="btn btn-ghost btn-sm" data-testid={`delete-btn-${r.routing_code}`}><Trash2 size={14} color="#DC2626" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {showModal && (
        <div className="modal-overlay" data-testid="routing-modal">
          <div className="modal-content" style={{ maxWidth: '700px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>{editing ? 'Edit Routing' : 'Create Routing'}</h2></div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Product *</label>
                  <select className="form-select" value={formData.product_sku} onChange={(e) => onProductSelect(e.target.value)} required data-testid="product-select">
                    <option value="">Select Product</option>
                    {inventory.filter(i => i.product_category === 'fg').map(i => <option key={i.sku} value={i.sku}>{i.sku} - {i.name}</option>)}
                  </select>
                </div>
                <div style={{ marginTop: '12px' }}>
                  <label className="form-label" style={{ fontWeight: 600 }}>Routing Steps</label>
                  <div className="flex gap-2" style={{ marginBottom: '8px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div style={{ flex: 2, minWidth: '150px' }}><input type="text" className="form-input" placeholder="Operation name" value={stepInput.operation} onChange={(e) => setStepInput({...stepInput, operation: e.target.value})} data-testid="step-op-input" /></div>
                    <div style={{ flex: 2, minWidth: '150px' }}>
                      <select className="form-select" value={stepInput.machine_id} onChange={(e) => setStepInput({...stepInput, machine_id: e.target.value})} data-testid="step-machine-select">
                        <option value="">Select Machine</option>
                        {machines.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                      </select>
                    </div>
                    <div style={{ flex: 1, minWidth: '80px' }}><input type="number" className="form-input" placeholder="Cycle(s)" value={stepInput.cycle_time_sec || ''} onChange={(e) => setStepInput({...stepInput, cycle_time_sec: parseInt(e.target.value) || 0})} /></div>
                    <div style={{ flex: 1, minWidth: '80px' }}><input type="number" className="form-input" placeholder="Setup(m)" value={stepInput.setup_time_min || ''} onChange={(e) => setStepInput({...stepInput, setup_time_min: parseInt(e.target.value) || 0})} /></div>
                    <button type="button" onClick={addStep} className="btn btn-secondary btn-sm" data-testid="add-step-btn"><Plus size={14} /></button>
                  </div>
                  {formData.steps.length > 0 && (
                    <div className="table-container" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      <table>
                        <thead><tr><th>#</th><th>Operation</th><th>Machine</th><th>Cycle(s)</th><th>Setup(m)</th><th></th></tr></thead>
                        <tbody>
                          {formData.steps.map((s, i) => (
                            <tr key={i}><td>{s.step_no}</td><td>{s.operation}</td><td>{s.machine_name}</td><td>{s.cycle_time_sec}</td><td>{s.setup_time_min}</td><td><button type="button" onClick={() => removeStep(i)} className="btn btn-ghost btn-sm"><X size={14} color="#DC2626" /></button></td></tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-routing-btn">{editing ? 'Update' : 'Create Routing'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoutingMaster;
